import { AnimatePresence, motion } from 'framer-motion';
import { ArrowBackIos, ArrowForwardIos } from '@material-ui/icons';
import {
  Box,
  Button,
  Container,
  Grid,
  Hidden,
  IconButton,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useStaticQuery, graphql, navigate } from 'gatsby';
import { wrap } from 'popmotion';
import * as React from 'react';

import Markdown from '../../styled/markdown';
import SocialSidebar from './social-sidebar';
import pattern from '../../../images/pattern.svg';

const variants = {
  enter: (direction) => ({
    x: direction > 0 ? '100vw' : '-100vw',
    opacity: 0,
  }),

  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    zIndex: 0,
    x: direction < 0 ? '100vw' : '-100vw',
    opacity: 0,
  }),
};

const useStyles = makeStyles((theme) => ({
  content: {
    zIndex: 1,
    position: 'relative',
    paddingTop: 0,
    top: 0,
    left: 0,
    width: '100%',
    maxHeight: '100vh',
    height: '100vh',
  },
  media: {
    objectFit: 'cover',
    position: 'absolute',
    paddingTop: 0,
    top: 0,
    left: 0,
    zIndex: 0,
    width: '100%',
    maxHeight: '100vh',
    height: '100vh',
  },
  social: {
    height: 'calc(95vh)',
  },
  socialSidebar: {
    height: 'calc(95vh)',
  },
  line: {
    margin: '0 auto',
    display: 'block',
    borderLeft: '2px solid white',
    height: theme.spacing(20),
  },
  divider: {
    display: 'block',
    width: theme.spacing(10),
    borderBottom: '2px solid white',
  },
  arrow: {
    color: 'white',
  },
  titleGrid: {
    height: 'calc(60vh)',
    width: '100%',
  },
  titleGridContainer: {
    marginTop: theme.spacing(-18),

    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(-20),
    },
  },
  subTitleGrid: {
    width: '100%',
  },
  slideDescription: {
    textTransform: 'uppercase',
    lineHeight: 'auto',
    color: 'inherit',
    fontWeight: 300,
  },
  pattern: {
    padding: theme.spacing(0, 0, 3, 0),
    width: 60,
  },
}));

/**
 * Experimenting with distilling swipe offset and velocity into a single variable, so the
 * less distance a user has swiped, the more velocity they need to register as a swipe.
 * Should accomodate longer swipes and short flicks without having binary checks on
 * just distance thresholds and velocity > 0.
 */
const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => Math.abs(offset) * velocity;

const query = graphql`
  query LandingPageQuery {
    contentfulInformacion {
      redesSociales {
        nombre
        id
        url
      }
    }
    contentfulLandingPage {
      titulo {
        titulo
      }
      slides {
        id
        color
        sombra
        nombre
        descripcionMd {
          descripcionMd
        }
        pieDePagina
        boton
        imagen {
          file {
            url
            fileName
            contentType
          }
          fluid(maxWidth: 1800) {
            srcWebp
            srcSetWebp
          }
        }
      }
    }
  }
`;

const LandingPage = () => {
  const { contentfulLandingPage, contentfulInformacion } =
    useStaticQuery(query);
  const classes = useStyles();

  const [[page, direction], setPage] = React.useState([0, 0]);

  // We only have 3 images, but we paginate them absolutely (ie 1, 2, 3, 4, 5...) and
  // then wrap that within 0-2 to find our image ID in the array below. By passing an
  // absolute page index as the `motion` component's `key` prop, `AnimatePresence` will
  // detect it as an entirely new image. So you can infinitely paginate as few as 1 images.
  const imageIndex = wrap(0, contentfulLandingPage.slides.length, page);

  const paginate = (newDirection) => {
    setPage([page + newDirection, newDirection]);
  };
  const next = () => paginate(1);
  const previous = () => paginate(-1);

  React.useEffect(() => {
    const changeSlide = setInterval(next, 5000);

    return () => clearInterval(changeSlide);
  }, [imageIndex]);

  return (
    <>
      <AnimatePresence>
        <motion.img
          key={page}
          className={classes.media}
          src={contentfulLandingPage.slides[imageIndex].imagen?.fluid?.srcWebp}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={1}
          onDragEnd={(e, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);

            if (swipe < -swipeConfidenceThreshold) {
              paginate(1);
            } else if (swipe > swipeConfidenceThreshold) {
              paginate(-1);
            }
          }}
        />
      </AnimatePresence>
      <div
        className={classes.content}
        style={{ color: contentfulLandingPage.slides[imageIndex].color }}
      >
        <Container maxWidth="xl">
          <Grid
            container
            spacing={3}
            direction="row"
            justifyContent="center"
            alignItems="center"
            className={classes.gridContainer}
          >
            <Hidden smDown>
              <Grid
                justifyContent="center"
                alignItems="center"
                item
                xs={12}
                lg={1}
                md={1}
                sm={12}
                className={classes.social}
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, type: 'spring' }}
                >
                  <SocialSidebar items={contentfulInformacion.redesSociales} />
                </motion.div>
              </Grid>
            </Hidden>

            <Grid item xs={12} sm={12} md={10} lg={10}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, type: 'spring' }}
              >
                <Box>
                  <Box
                    className={classes.titleGrid}
                    display="flex"
                    alignItems="center"
                  >
                    <Box componet="div">
                      <Box
                        component="div"
                        className={classes.titleGridContainer}
                      >
                        <Markdown
                          className="background-sh"
                          shadow={
                            contentfulLandingPage.slides[imageIndex].sombra
                          }
                        >
                          {
                            contentfulLandingPage.slides[imageIndex]
                              .descripcionMd.descripcionMd
                          }
                        </Markdown>
                      </Box>
                      <Box component="div">
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => navigate('/search')}
                        >
                          {contentfulLandingPage.slides[imageIndex].boton ||
                            'Conoce más...'}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                  <Box item xs={12}>
                    <img
                      src={pattern}
                      alt={
                        contentfulLandingPage?.slides[imageIndex].descripcion
                      }
                      className={classes.pattern}
                      style={
                        contentfulLandingPage.slides[imageIndex].color ===
                        'white'
                          ? {
                              filter: 'invert(1)',
                            }
                          : {}
                      }
                    />
                    <Typography
                      variant="subtitle1"
                      className={classes.slideDescription}
                      style={{
                        color: contentfulLandingPage?.slides[imageIndex].color,
                      }}
                    >
                      {contentfulLandingPage?.slides[imageIndex].pieDePagina}
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </Grid>

            <Hidden smDown>
              <Grid item xs={12} sm={12} md={1} lg={1}>
                {contentfulLandingPage?.slides.length > 0 && (
                  <Box
                    display="flex"
                    component="div"
                    className={classes.socialSidebar}
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="column"
                  >
                    <IconButton onClick={next} className={classes.arrow}>
                      <ArrowForwardIos />
                    </IconButton>
                    <span className={classes.divider} />
                    <IconButton onClick={previous} className={classes.arrow}>
                      <ArrowBackIos />
                    </IconButton>
                  </Box>
                )}
              </Grid>
            </Hidden>
          </Grid>
        </Container>
      </div>
    </>
  );
};

export default LandingPage;
